import React, { Fragment, useEffect, useState } from 'react'
import Button from 'portal-modules/common/components/Button'
import { PortalModal } from 'portal-modules/common/components/modals/portal-modal'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import Api from 'App/api'
import { TaxDetails } from 'portal-modules/common/components'
import { Document } from '@entrata/portals-common/lib/icons'
import {
  FocusSpan,
  underlineFocus,
} from 'portal-modules/common/components/focus-styles'
import { InvoiceDocumentModal } from 'portal-modules/ledger/components/modals'
import { Overlay } from '@entrata/portals-common/lib/components'

const StyledSection = styled('section')`
  display: flex;
  justify-content: space-between;
  padding: ${({ hasTaxes }) => (hasTaxes ? '4px 0' : '8px 0')};
`

const StyledListItem = styled('li')`
  display: flex;
  justify-content: space-between;
  padding-bottom: 8px;

  :last-child {
    padding-bottom: 0;
  }
`

const StyledTotaLi = styled(StyledListItem)`
  font-weight: bold;
  padding-top: 8px;
`

const ChargeTitle = styled('span')`
  padding-top: 8px;
`

const StyledTaxDisclaimer = styled('span')`
  color: ${({ theme }) => theme.gray.medium};
  display: flex;
  font-size: 12px;
  font-style: italic;
`

const StyledTaxAmountTotal = styled('section')`
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.gray.medium};
  font-size: 12px;
  margin-top: 12px;
`

const StyledTaxDetails = styled(TaxDetails)`
  section {
    width: 100%;
  }
`
const StyledIconButton = styled('button')`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.secondary.base};
  width: 100%;
  ${underlineFocus};
  &:not(:last-child) {
    margin-right: 8px;
    margin-left: 8px;
  }
  svg {
    margin-right: 5px;
  }
  span {
    font-weight: bold;
    font-size: 16px;
  }
`

const ChargeDetailsModal = props => {
  const {
    arCodeId,
    isPayment,
    createdByName,
    tax_exclusive,
    customer_invoice_id,
    document_id,
    token,
    history,
    type,
    onClose,
  } = props
  const [overlay, setOverlayToggle] = useState(false)
  let taxTotalAmt = 0
  let totalAmt = 0
  let ShowTaxDislaimer = false

  const [chargeDetails, setChargeDetails] = useState({
    details: [{ unit_number: '', amount: '' }],
  })

  useEffect(() => {
    if (arCodeId) {
      Api.getChargeDetails({ ar_code_id: arCodeId }).then(response => {
        setChargeDetails(response)
      })
    }
  }, [arCodeId])

  return (
    <PortalModal
      title={__('Grouped Charge Details')}
      {...props}
      footerContent={
        <Button size='medium' appearance='primary' onClick={props.onClose} fullWidth>
          {__('Close')}
        </Button>
      }
      isWideDialog
    >
      <ChargeTitle>{props.chargeTitle}</ChargeTitle>
      {isPayment && createdByName && (
        <StyledSection>
          <span>{__('Submitted By')}</span>
          <span>{__('{%s,0}', [createdByName])}</span>
        </StyledSection>
      )}

      {chargeDetails.details.map(({ unit_number, amount, taxes }, i) => {
        const hasTaxesAmt = taxes && !!taxes.length

        !ShowTaxDislaimer && hasTaxesAmt && (ShowTaxDislaimer = true)

        let chargeTaxTotalAmt =
          (hasTaxesAmt &&
            Object.values(taxes).reduce(
              (chargeTaxTotalAmt, tax) => chargeTaxTotalAmt + tax.tax_amount,
              0,
            )) ||
          0
        totalAmt =
          (chargeTaxTotalAmt != 0 &&
            +totalAmt + +amount + +chargeTaxTotalAmt) ||
          +totalAmt + +amount
        let chargeAmt =
          (hasTaxesAmt &&
            !tax_exclusive &&
            chargeTaxTotalAmt != 0 &&
            +amount + +chargeTaxTotalAmt) ||
          amount

        return (
          <Fragment key={i}>
            <StyledListItem>
              <span>{__('Unit {%s, 0}', [unit_number])}</span>
              <span>{i18nUtils.currency(chargeAmt, 'p:2')}</span>
            </StyledListItem>

            {hasTaxesAmt &&
              tax_exclusive &&
              taxes.map(({ id, tax_amount, tax_label }, j) => {
                taxTotalAmt = taxTotalAmt + tax_amount
                return (
                  <StyledListItem key={j}>
                    <StyledTaxDetails
                      key={id}
                      tax_label={tax_label}
                      tax_amount={tax_amount}
                    />
                  </StyledListItem>
                )
              })}
          </Fragment>
        )
      })}
      {!tax_exclusive && ShowTaxDislaimer && (
        <StyledTaxDisclaimer>
          <span>{__('Taxes included in charges')}</span>
        </StyledTaxDisclaimer>
      )}
      {taxTotalAmt != 0 && tax_exclusive && (
        <StyledTaxAmountTotal>
          <span>{__('Total Taxes')}</span>
          <span>{__('({%m, 0, p:2} tax)', [taxTotalAmt])}</span>
        </StyledTaxAmountTotal>
      )}

      <StyledTotaLi>
        <span>{__('Total')}</span>
        {i18nUtils.currency(totalAmt || 0, 'p:2')}
      </StyledTotaLi>
      {!!customer_invoice_id &&
        !!document_id &&
        ['Charge', 'Credit', 'charge', 'credit'].includes(type) && (
          <Fragment>
            <hr />
            <StyledSection>
              <Overlay show={overlay} />
              <StyledIconButton onClick={() => setOverlayToggle(true)}>
                <FocusSpan>
                  <Document size={15} />
                  {__('View Invoice')}
                </FocusSpan>
              </StyledIconButton>
              {overlay && (
                <InvoiceDocumentModal
                  show={overlay}
                  onClose={() => {
                    onClose()
                    setOverlayToggle(false)
                  }}
                  title={__('Customer Invoice - {%s, 0}', [
                    customer_invoice_id,
                  ])}
                  document_id={document_id}
                  token={token}
                  history={history}
                />
              )}
            </StyledSection>
          </Fragment>
        )}
    </PortalModal>
  )
}

ChargeDetailsModal.propTypes = {
  arCodeId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
  chargeTitle: PropTypes.string,
  balance: PropTypes.number,
  isPayment: PropTypes.bool,
  createdByName: PropTypes.string,
  tax_exclusive: PropTypes.bool,
  customer_invoice_id: PropTypes.number,
  document_id: PropTypes.number,
  token: PropTypes.string,
  history: PropTypes.object,
}

export default ChargeDetailsModal
